import React, { useEffect, useRef } from 'react'
import { type RootState } from '../../../store'
import { type ConnectedProps, connect } from 'react-redux'
import { useWebSocket } from '../../../contexts/WebSocketContext'
import { Box, Button, ButtonGroup, CircularProgress, Typography } from '@mui/material'
import { getAlertCountDashboardService } from '../redux/actionCreator'
import { activeAlertCountSocketUpdateAction, type AlertCountDataState } from '../redux/getAlertCountAssetDashbordSlice'
import ApexCharts from 'react-apexcharts'
import LanguageStrings from '../../../i18n/locales'
import moment from 'moment-timezone'
import AuthError from '../../../components/ErrorComponents/AuthError'

const AssetAlertDuration = (props: AllProps): JSX.Element => {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const { messages, subscribeToAlertDurationStatus, isConnected } = useWebSocket()
  const lastPayloadRef = useRef<string | null>(null)
  const severities = { all: '', low: 'low', high: 'high', urgent: 'urgent' }
  const [alertLevel, setAlertLevel] = React.useState('') // Default alert level
  const AssetDashboardStrings = LanguageStrings().AssetDashboardStrings
  const [alertCountsData, setAlertCountsData] = React.useState<any>([])

  // alert count chart setup
  const alertChartOptions = {
    chart: {
      type: 'bar' as const,
      height: 360,
      backgroundColor: '#272727',
      toolbar: {
        show: true,
        tools: {
          download: false
        }
      }
    },
    title: {
      text: '',
      style: {}
    },
    xaxis: {
      categories: alertCountsData?.map((item: any) => item.parameterName),
      title: {
        text: 'hours (hours:minutes)',
        style: {
          color: '#D0D0D0',
          fontFamily: '"Honeywell Sans Web", Arial, Helvetica, sans-serif',
          fontSize: '14px',
          margin: 15
        }
      },
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      tickAmount: alertCountsData.every((item: { timeDifference: number }) => item.timeDifference === 0.01) ? 1 : 5,
      labels: {
        show: true,
        style: {
          colors: '#D0D0D0',
          fontFamily: '"Honeywell Sans Web", Arial, Helvetica, sans-serif',
          fontSize: '13px'
        },
        formatter: function (val: any) {
          return `${Math.floor(val)}:${String(val.toFixed(2)).split('.')[1]}`
        }
      }
    },
    yaxis: {
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        show: true,
        style: {
          colors: '#D0D0D0',
          fontFamily: '"Honeywell Sans Web", Arial, Helvetica, sans-serif',
          fontSize: '13px'
        }
      }
    },
    dataLabels: {
      enabled: true,
      offsetX: 4,
      textAnchor: 'start' as const,
      style: {
        colors: ['#fff']
      },
      formatter: function (val: number) {
        return `${Math.floor(val)}:${String(val.toFixed(2)).split('.')[1]}`
      }
    },
    legend: {
      show: true,
      position: 'bottom' as const,
      labels: {
        colors: '#D0D0D0',
        fontFamily: '"Honeywell Sans Web", Arial, Helvetica, sans-serif',
        fontSize: '14px',
        useSeriesColors: false
      }
    },
    series: [
      {
        name: 'Duration',
        data: alertCountsData.map((entry: any) => ({
          x: entry.parameterName,
          y: entry.timeDifference
        }))
      }
    ],
    noData: {
      text: 'No Data Available',
      offsetX: 20,
      offsetY: -10,
      style: {
        color: '#F2F2F2',
        fontSize: '14px'
      }
    },
    credits: {
      enabled: false
    },
    tooltip: {
      enabled: true,
      theme: 'dark',
      shared: true,
      intersect: false,
      y: {
        formatter: (val: number, { seriesIndex, dataPointIndex, w }: { seriesIndex: number, dataPointIndex: number, w: any }) => {
          return `${Math.floor(val)}:${String(val.toFixed(2)).split('.')[1]} hours`
        }
      }
    },
    plotOptions: {
      bar: {
        columnWidth: '50%',
        minHeight: 5,
        horizontal: true,
        borderRadius: 0,
        borderRadiusApplication: 'end' as const,
        borderRadiusWhenStacked: 'last' as const,
        barHeight: '20%',
        distributed: false,
        rangeBarOverlap: true,
        rangeBarGroupRows: false,
        hideZeroBarsWhenGrouped: false,
        isDumbbell: false,
        isFunnel: false,
        isFunnel3d: true,
        colors: {
          ranges: [{ from: 0, to: 0, color: undefined }],
          backgroundBarColors: [],
          backgroundBarOpacity: 1,
          backgroundBarRadius: 0
        },
        dataLabels: {
          position: 'insideRight',
          offsetX: -50,
          textAnchor: 'start' as const,
          maxItems: 100,
          hideOverflowingLabels: true,
          orientation: 'horizontal' as const,
          style: {
            colors: ['#fff']
          },
          formatter: function (val: number) {
            return `${Math.floor(val)}:${String(val.toFixed(2)).split('.')[1]}`
          },
          total: {
            enabled: false,
            style: {
              color: '#373d3f',
              fontSize: '12px',
              fontWeight: 600
            }
          }
        }
      }
    }
  }

  const reCalculate = (): void => {
    if (props.AlertCountDataAssetDashboard.AlertCountDataAssetDashboard !== null) {
      const calculateTimeDifference = (alarmTime: string): number => {
        const date = moment.utc(alarmTime).tz(userTimezone).format('YYYY-MM-DD hh:mm:ss A')
        const difference = new Date().getTime() - new Date(date).getTime()
        const hours = Math.floor(difference / (1000 * 60 * 60))
        let minutes: string | number = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60))
        if (minutes < 10) {
          // eslint-disable-next-line
            minutes = '0' + minutes
        }
        return parseFloat(`${hours}.${minutes}`)
      }
      if (Array.isArray(props.AlertCountDataAssetDashboard.AlertCountDataAssetDashboard)) {
        const updatedData = props.AlertCountDataAssetDashboard.AlertCountDataAssetDashboard
          .filter(entry => entry.parameterName !== null) // Skip entries with null parameterName
          .map(entry => ({
            ...entry,
            timeDifference: calculateTimeDifference(entry.alarmRaisedAt)
          }))

        setAlertCountsData(updatedData)
      }
    }
  }

  useEffect(() => {
    reCalculate()
  }, [props.AlertCountDataAssetDashboard])

  // Subscribe to alert duration status only once or when parentAssetId changes
  React.useEffect(() => {
    if (isConnected && props.parentAssetId !== '') {
      if (lastPayloadRef.current !== props.parentAssetId) {
        lastPayloadRef.current = props.parentAssetId
        subscribeToAlertDurationStatus(props.parentAssetId)
          .catch(() => {
            // Handle ModBus subscription error if needed
          })
      }
    }
  }, [isConnected, props.parentAssetId])

  React.useEffect(() => {
    const interval = setInterval(() => {
      reCalculate()
    }, 20000)
    return () => {
      clearInterval(interval)
    }
  }, [props.AlertCountDataAssetDashboard])

  const fetchData = (): void => {
    setAlertLevel('')
    props.getAlertCountDashboardService(props.parentSiteId, props.parentAssetId, '')
  }

  React.useEffect(() => {
    fetchData()
  }, [props.Golive, props.parentAssetId, props.parentSiteId, props.tabSwitch])

  React.useEffect(() => {
    setAlertLevel('')
  }, [props.startDate, props.endDate])

  React.useEffect(() => {
    if (Array.isArray(messages)) {
      // Check if the latest message is a HEARTBEAT and return early
      if (messages.length > 0 && messages[messages.length - 1]?.type === 'HEARTBEAT') {
        return
      }

      const alertCountMessages = messages.filter(
        (msg: any) => msg?.type === 'DASHBOARD_ACTIVE_ALERT_COUNT_UPDATE'
      )
      if (
        alertCountMessages.length > 0 &&
        Array.isArray(alertCountMessages[alertCountMessages.length - 1].data) &&
        alertCountMessages[alertCountMessages.length - 1].data.length > 0 &&
        alertCountMessages[alertCountMessages.length - 1].data[0].parentAssetId === props.parentAssetId
      ) {
        const latestAlertCountMessage = alertCountMessages[alertCountMessages.length - 1]
        if (latestAlertCountMessage !== null && latestAlertCountMessage !== undefined && alertLevel === '') {
          props.activeAlertCountSocketUpdateAction(latestAlertCountMessage)
        }
      }
    }
  }, [messages])

  const handleAlertLevelChange = (level: string): void => {
    setAlertLevel(level)
    props.getAlertCountDashboardService(props.parentSiteId, props.parentAssetId, level)
  }
  const isDataEmpty = props.AlertCountDataAssetDashboard.AlertCountDataAssetDashboard === null || !Array.isArray(props.AlertCountDataAssetDashboard.AlertCountDataAssetDashboard) || props.AlertCountDataAssetDashboard.AlertCountDataAssetDashboard.length === 0

  return (<>
    <Box
                    sx={{
                      backgroundColor: '#272727',
                      padding: '10px',
                      borderRadius: '8px',
                      flex: 1
                    }}
                  >
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                      <Typography className='title-box' style={{ marginBottom: '20px' }}>{AssetDashboardStrings.acriveAlertCount}</Typography>
                      </div>
                    </div>
                    <ButtonGroup className='range-handle' color="primary" aria-label="outlined primary button group" sx={{ mb: 2 }}>
                      <Button
                        variant={alertLevel === severities.all ? 'contained' : 'outlined'}
                        onClick={() => { handleAlertLevelChange(severities.all) }}
                      >
                        All
                      </Button>
                      <Button
                        variant={alertLevel === severities.low ? 'contained' : 'outlined'}
                        onClick={() => { handleAlertLevelChange(severities.low) }}
                      >
                        Low
                      </Button>
                      <Button
                        variant={alertLevel === severities.high ? 'contained' : 'outlined'}
                        onClick={() => { handleAlertLevelChange(severities.high) }}
                      >
                        High
                      </Button>
                      <Button
                        variant={alertLevel === severities.urgent ? 'contained' : 'outlined'}
                        onClick={() => { handleAlertLevelChange(severities.urgent) }}
                      >
                        Urgent
                      </Button>
                    </ButtonGroup>
                    {
                      props.AlertCountDataAssetDashboard.isLoading
                        ? <div style={{ display: 'flex', height: '350px', justifyContent: 'center', alignItems: 'center' }} ><CircularProgress /></div>
                        : (isDataEmpty && props.AlertCountDataAssetDashboard.httpStatus === 200)
                            ? <div style={{ display: 'flex', height: '350px', justifyContent: 'center', alignItems: 'center', color: '#f0f0f0' }}>
                            <AuthError
                                className='alertTrendError'
                                errorMessage='NoDataPresentAlertDuration'
                              /></div>
                            : <ApexCharts
                            options={alertChartOptions}
                            series={alertChartOptions.series}
                            type={alertChartOptions.chart.type}
                            height={350}
                          />
                    }

    </Box>
  </>)
}

interface DispatchToProps {
  getAlertCountDashboardService: (siteId: string, assetId: string, severity: string) => void
  activeAlertCountSocketUpdateAction: (payload: any) => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  getAlertCountDashboardService: (siteId: string, assetId: string, severity: string) => dispatch(getAlertCountDashboardService(siteId, assetId, severity)),
  activeAlertCountSocketUpdateAction: (payload: any) => dispatch(activeAlertCountSocketUpdateAction(payload))
})

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface StateToProps {
  AlertCountDataAssetDashboard: AlertCountDataState
}

const mapStateToProps = (state: RootState): StateToProps => ({
  AlertCountDataAssetDashboard: state.AlertCountDataAssetDashboard
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

interface InputProps {
  parentSiteId: string
  parentAssetId: string
  Golive: boolean
  startDate: string
  endDate: string
  tabSwitch?: string
}

type AllProps = PropsFromRedux & InputProps

export const MemoAssetAlertDuration = React.memo(connector(AssetAlertDuration))
